import { useState } from 'react';
import { formatClocks } from '@/lib/utils';

/** Kurse der gewählten Unterrichtsorte. Ohne Ort gibt es keine Kursliste. */
export function coursesAtLocations(courses, locationIds) {
  const wanted = new Set((locationIds || []).map(String).filter(Boolean));
  if (!wanted.size) return [];
  return (courses || []).filter((course) => wanted.has(String(course.location_id)));
}

export function coursesAtLocation(courses, locationId) {
  return coursesAtLocations(courses, locationId ? [locationId] : []);
}

export function toggleId(ids, id) {
  const next = new Set(ids || []);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return [...next];
}

export function addId(ids, id) {
  if (!id || (ids || []).includes(id)) return ids || [];
  return [...(ids || []), id];
}

function courseLabel(course) {
  const name = formatClocks(course.name) || course.name;
  return `${name}${course.teacher_name ? ` (${course.teacher_name})` : ''}`;
}

export function CourseContextFields({
  locations, courses, locationIds, courseIds, onLocations, onCourses,
}) {
  const [activeLocationId, setActiveLocationId] = useState('');
  const chosenLocations = locationIds || [];
  const chosenCourses = courseIds || [];
  const placeId = chosenLocations.includes(activeLocationId)
    ? activeLocationId
    : (chosenLocations[chosenLocations.length - 1] || '');
  const atPlace = coursesAtLocation(courses, placeId);
  const available = atPlace.filter((course) => !chosenCourses.includes(course.id));
  const coursePlaceholder = !placeId
    ? 'Zuerst einen Ort wählen'
    : (atPlace.length ? 'Kurs wählen…' : 'Keine Kurse an diesem Ort');

  const choosePlace = (id) => {
    setActiveLocationId(id);
    if (id) onLocations(addId(chosenLocations, id));
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="correspondence-course-context">
      <div>
        <label className="text-xs font-medium text-slate-500">Unterrichtsort</label>
        <select
          value={placeId}
          onChange={(e) => choosePlace(e.target.value)}
          className="mt-1 h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm"
          data-testid="correspondence-location-select"
        >
          <option value="">Ort wählen…</option>
          {(locations || []).map((loc) => (
            <option key={loc.id} value={loc.id}>{loc.name}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="text-xs font-medium text-slate-500">
          Kurs{chosenCourses.length ? ` (${chosenCourses.length})` : ''}
        </label>
        <select
          value=""
          onChange={(e) => { if (e.target.value) onCourses(addId(chosenCourses, e.target.value)); }}
          disabled={!placeId || !atPlace.length}
          className="mt-1 h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm disabled:bg-slate-50"
          data-testid="correspondence-course-select"
        >
          <option value="">{coursePlaceholder}</option>
          {available.map((course) => (
            <option key={course.id} value={course.id}>{courseLabel(course)}</option>
          ))}
        </select>
      </div>
      {!!chosenLocations.length && (
        <div className="sm:col-span-2 space-y-2" data-testid="correspondence-selection">
          {chosenLocations.map((id) => {
            const loc = (locations || []).find((item) => item.id === id);
            const picked = (courses || []).filter(
              (course) => String(course.location_id) === String(id) && chosenCourses.includes(course.id),
            );
            return (
              <div key={id} className="rounded-md border border-slate-200 px-2 py-1.5" data-testid={`correspondence-location-${id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-slate-800">{loc?.name || id}</span>
                  <button
                    type="button"
                    className="text-slate-400 hover:text-slate-700"
                    onClick={() => onLocations(chosenLocations.filter((item) => item !== id))}
                    data-testid={`correspondence-location-remove-${id}`}
                  >
                    ×
                  </button>
                </div>
                {picked.length ? (
                  <ul className="mt-1 space-y-0.5">
                    {picked.map((course) => (
                      <li key={course.id} className="flex items-center justify-between gap-2 text-sm text-slate-600" data-testid={`correspondence-course-${course.id}`}>
                        <span>{courseLabel(course)}</span>
                        <button
                          type="button"
                          className="text-slate-400 hover:text-slate-700"
                          onClick={() => onCourses(chosenCourses.filter((item) => item !== course.id))}
                          data-testid={`correspondence-course-remove-${course.id}`}
                        >
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-0.5 text-xs text-slate-400">
                    {coursesAtLocation(courses, id).length ? 'Noch kein Kurs gewählt' : 'Keine Kurse an diesem Ort'}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
